import {
  alphaSessionToken,
  alphaSessionUserId,
  heldAlphaPopupSession,
  holdAlphaPopupSession,
} from 'services/alpha/alphaSession';

export const AUTH_REQUEST_MESSAGE = 'mspp-alpha-auth-request';
export const AUTH_MESSAGE = 'mspp-alpha-auth';
export const NONCE_PATTERN = /^[a-f0-9]{32}$/;
export const AUTH_TIMEOUT_MS = 3000;

export interface IPopupAuthPayload {
  accessToken: string;
  userId: string;
}

export interface IPopupAuthReply extends IPopupAuthPayload {
  type: typeof AUTH_MESSAGE;
  nonce: string;
  context?: unknown;
}

export type PopupAuthResult =
  | { status: 'handoff'; auth: IPopupAuthPayload; context?: unknown }
  | { status: 'existing' }
  | { status: 'none' };

export const createNonce = (): string => {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
};

export const readOwnAuth = (): IPopupAuthPayload | null => {
  try {
    const accessToken = alphaSessionToken();
    const userId = alphaSessionUserId();
    return accessToken && userId ? { accessToken, userId } : null;
  } catch {
    return null;
  }
};

export const storeAuth = (auth: IPopupAuthPayload): void => {
  holdAlphaPopupSession(auth);
};

const adoptOwnAuth = (): PopupAuthResult => {
  if (heldAlphaPopupSession()) return { status: 'existing' };
  const own = readOwnAuth();
  if (!own) return { status: 'none' };
  storeAuth(own);
  return { status: 'existing' };
};

export const buildAuthReply = (nonce: string, context?: unknown): IPopupAuthReply | null => {
  const own = readOwnAuth();
  if (!own) return null;
  const reply: IPopupAuthReply = { type: AUTH_MESSAGE, nonce, ...own };
  if (context !== undefined) reply.context = context;
  return reply;
};

export const parseAuthReply = (
  event: { origin: string; source: unknown; data: unknown },
  nonce: string,
): { auth: IPopupAuthPayload; context?: unknown } | null => {
  if (event.origin !== window.location.origin) return null;
  if (!window.opener || event.source !== window.opener) return null;
  const data = event.data as Partial<IPopupAuthReply> | null;
  if (!data || typeof data !== 'object' || data.type !== AUTH_MESSAGE) return null;
  if (data.nonce !== nonce) return null;
  if (typeof data.accessToken !== 'string' || !data.accessToken) return null;
  if (typeof data.userId !== 'string' || !data.userId) return null;
  return {
    auth: { accessToken: data.accessToken, userId: data.userId },
    context: data.context,
  };
};

export const requestAuthFromOpener = (timeoutMs = AUTH_TIMEOUT_MS): Promise<PopupAuthResult> =>
  new Promise(resolve => {
    const opener = window.opener as Window | null;
    if (!opener || opener === window || opener.closed) {
      resolve(adoptOwnAuth());
      return;
    }

    const nonce = createNonce();
    let timer = 0;

    const finish = (result: PopupAuthResult): void => {
      window.removeEventListener('message', onMessage);
      window.clearTimeout(timer);
      resolve(result);
    };

    function onMessage (event: MessageEvent): void {
      const reply = parseAuthReply(event, nonce);
      if (!reply) return;
      storeAuth(reply.auth);
      finish({ status: 'handoff', auth: reply.auth, context: reply.context });
    }

    window.addEventListener('message', onMessage);
    timer = window.setTimeout(() => { finish(adoptOwnAuth()); }, timeoutMs);
    try {
      opener.postMessage({ type: AUTH_REQUEST_MESSAGE, nonce }, window.location.origin);
    } catch {
      finish(adoptOwnAuth());
    }
  });
